import React, { useCallback, useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import DashboardActionCard from '../cards/dashboard-action-card.component';
import RegisterAdminModal from './register-admin-modal.component';
import type DashboardOffice from '../models/dashboard-office';
import AppConstants from '../../../core/app.constants';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import notify from '../../../core/components/notify/notify.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import { registerAdmin } from '../../authentication/registration/registration.service';
import DeleteOfficeModal from '../../offices/modals/delete-office-modal.component';
import PermittedAdmin from '../../offices/models/permitted-admin';
import { deleteOfficeById } from '../../offices/services/offices.service';
import Grid from '../../../core/components/grid/grid.component';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly showModal: boolean;
  readonly dashboardOffice: DashboardOffice;
  readonly handleClose: () => void;
  readonly handleDidUpdateOffice: (dashboardOffice: DashboardOffice) => void;
  readonly handleDidDeleteOffice: (dashboardOffice: DashboardOffice) => void;
}

const DashboardOfficeModal = ({
  showModal,
  dashboardOffice,
  handleClose,
  handleDidUpdateOffice,
  handleDidDeleteOffice,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [adminToRegister, setAdminToRegister] = useState(new PermittedAdmin());

  // Exactly one message either way: the service reports nothing itself, so a failure is not toasted twice.
  const deleteOffice = useCallback(async (acknowledged: boolean): Promise<void> => {
    if (dashboardOffice?.id != null) {
      const outcome = await deleteOfficeById(dashboardOffice.id, keycloak, acknowledged);
      if (outcome.deleted) {
        notify(t('office-delete-success-message'), true);
        handleDidDeleteOffice(dashboardOffice);
        handleClose();
      } else {
        notify(t(outcome.errorKey ?? 'office-delete-error-message'), false);
      }
    } else {
      notify(t('office-delete-error-message'), false);
    }
    setShowDeleteModal(false);
  }, [dashboardOffice, handleClose, handleDidDeleteOffice, keycloak, t]);

  const handleRegisterAdmin = useCallback(async (): Promise<void> => {
    if (!stringIsNullOrWhitespace(adminToRegister.email)) {
      const { email } = adminToRegister;
      const role = AppConstants.ADMIN_ROLE;
      const response = await registerAdmin({ email, role }, keycloak);
      if (response.success) {
        notify(t('administrator-registration-success-message'), true);
        const addedAccount = dashboardOffice.permittedAdmins.find((x) => x.email === email);
        if (addedAccount != null) {
          addedAccount.hasAccount = true;
        }
        handleDidUpdateOffice(dashboardOffice);
      } else {
        notify(t('administrator-registration-error-message'), false);
      }
    } else {
      notify(t('administrator-registration-error-message'), false);
    }
    setShowRegisterModal(false);
  }, [adminToRegister, dashboardOffice, handleDidUpdateOffice, keycloak, t]);

  const createdAtDateString = (): string => {
    const date = new Date(dashboardOffice.createdAt);
    return date > new Date(1) ? date.toLocaleDateString() : t('unknown');
  };

  return (
    <>
      <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
        <Column crossAxisAlignment={CrossAxisAlignment.Stretch} itemSpacing={12}>
          <span className={style(styles.columnHeader)}>{dashboardOffice.name}</span>
          <Grid
            numberOfColumns={1}
            numberOfItems={dashboardOffice.permittedAdmins.filter((permittedAdmin) => !permittedAdmin.hasAccount).length}
            renderItem={(index): React.JSX.Element => {
              const permittedAdmin = dashboardOffice.permittedAdmins
                .filter((item) => !item.hasAccount)
                .sort((a, b) => (a.email > b.email ? 1 : -1))[index];
              return (
                permittedAdmin && (
                  <div key={permittedAdmin.email}>
                    <DashboardActionCard
                      text={`${permittedAdmin.email} ${t('unregistered-user-message')}`}
                      onClick={(): void => {
                        setAdminToRegister(permittedAdmin);
                        setShowRegisterModal(true);
                      }}
                    />
                  </div>
                )
              );
            }}
          />
          <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
            <span className={style(styles.modalTitle)}>{t('number-of-users')}</span>
            <span className={style(styles.modalBody)}>{dashboardOffice.permittedUserAmount}</span>
          </Row>
          <Row mainAxisAlignment={MainAxisAlignment.Stretch} crossAxisAlignment={CrossAxisAlignment.Center}>
            <span className={style(styles.modalTitle)}>{t('owner')}</span>
            <span className={style(styles.modalBody)}>{dashboardOffice.authorEmail}</span>
          </Row>
          <Row mainAxisAlignment={MainAxisAlignment.Stretch} crossAxisAlignment={CrossAxisAlignment.Center}>
            <span className={style(styles.modalTitle)}>{t('highest-amount-monthly-users')}</span>
            <span className={style(styles.modalBody)}>{dashboardOffice.highestPermittedUserAmountThisMonth}</span>
          </Row>
          <Row mainAxisAlignment={MainAxisAlignment.Stretch} crossAxisAlignment={CrossAxisAlignment.Center}>
            <span className={style(styles.modalTitle)}>{t('start-date')}</span>
            <span className={style(styles.modalBody)}>{createdAtDateString()}</span>
          </Row>
          {dashboardOffice.permittedAdmins.length > 0 && (
            <Column className={style(styles.modalList)}>
              <span className={style(styles.modalTitle)}>{t('extra-administrators')}</span>
              <Grid
                numberOfColumns={1}
                numberOfItems={dashboardOffice.permittedAdmins.length}
                renderItem={(index: number): React.JSX.Element => {
                  const permittedAdmin = dashboardOffice.permittedAdmins.sort((a, b) => (a.email > b.email ? 1 : -1))[index];
                  return (
                    permittedAdmin && (
                      <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={permittedAdmin.email}>
                        <span className={style(styles.modalBody)}>{permittedAdmin.email}</span>
                      </div>
                    )
                  );
                }}
              />
            </Column>
          )}
          <Row
            className={style(styles.deleteButton)}
            mainAxisAlignment={MainAxisAlignment.Stretch}
            crossAxisAlignment={CrossAxisAlignment.Center}
            onClick={(): void => {
              setShowDeleteModal(true);
            }}
          >
            <span>{t('delete-office')}</span>
            <DeleteIcon className={style(styles.deleteIcon)} />
          </Row>

          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('close')}
            </PrimaryButton>
          </div>
        </Column>
      </Modal>
      <DeleteOfficeModal
        showModal={showDeleteModal}
        officeId={dashboardOffice.id}
        officeName={dashboardOffice.name}
        handleAccept={(acknowledged): Promise<void> => deleteOffice(acknowledged)}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
      <RegisterAdminModal
        showModal={showRegisterModal}
        handleAccept={(): void => {
          handleRegisterAdmin();
        }}
        handleClose={(): void => {
          setShowRegisterModal(false);
        }}
      />
    </>
  );
};

export default DashboardOfficeModal;
